import { useRecoilState, useRecoilValue } from "recoil"
import { Flex, Box } from "@chakra-ui/react"
import {
  ElementRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import Countdown, { CountdownRenderProps } from "react-countdown"

import {
  timerOverlayAtom,
  TIMER_OVERLAY_STATE,
  TIMER_DIRECTION,
  timerOverlayCompletedTimeAtom
} from "../../../recoil/atoms/timer-overlay"
import { brandingSelector } from "../../../recoil/atoms/room"
import { CountdownTimeDeltaFn } from "react-countdown/dist/Countdown"
import { ConsoleLogger } from "../../../util/logger"
import { useTranslation } from "react-i18next"

const logger = new ConsoleLogger()

interface TimerProps {
  maxWidth: number
  isMobile: boolean
  isSmallMainScreen: boolean
  isTablet: boolean
}

const RenderTimer = ({
  days,
  hours,
  minutes,
  seconds,
  isSmallWidthMainScreen
}: CountdownRenderProps & { isSmallWidthMainScreen: boolean }) => {
  const roomStyles = useRecoilValue(brandingSelector)

  const array = [
    { text: "Days", data: days },
    { text: "Hours", data: hours },
    { text: "Min", data: minutes },
    { text: "Sec", data: seconds }
  ]
  return (
    <Box>
      {array.map(item => {
        // eslint-disable-next-line eqeqeq
        if (item.data == 0 && item.text !== "Sec") {
          return null
        }

        return (
          <Box
            as="span"
            display="inline-block"
            color={roomStyles?.commentTextColor || "white"}
            key={`render-timer-${item.text}`}
          >
            <Flex
              flexDirection="column"
              margin={"0 5px"}
              width={isSmallWidthMainScreen ? "30px" : "36px"}
              flexWrap="nowrap"
              alignItems={"center"}
              justifyContent="center"
            >
              <Flex
                width={isSmallWidthMainScreen ? "30px" : "36px"}
                maxHeight="40px"
                fontWeight="700"
                fontSize={isSmallWidthMainScreen ? "14px" : "20px"}
                lineHeight={1}
                textAlign="center"
                alignItems="center"
                justifyContent="center"
              >
                {item.data}
              </Flex>
              <Flex
                width={isSmallWidthMainScreen ? "30px" : "36px"}
                fontSize={isSmallWidthMainScreen ? "10px" : "15px"}
                textAlign="center"
                alignItems="center"
                justifyContent="center"
              >
                {item.text}
              </Flex>
            </Flex>
          </Box>
        )
      })}
    </Box>
  )
}

export const TimerOverlay: React.FC<TimerProps> = memo(
  ({ maxWidth, isSmallMainScreen, isMobile, isTablet }) => {
    const { t } = useTranslation()
    const roomStyles = useRecoilValue(brandingSelector)
    const [isCompleted, setIsCompleted] = useState(false)
    const countdownEleRef = useRef<ElementRef<typeof Countdown>>(null)
    const { allowedDuration, timerDirection, timerOverlayState, usedDuration } =
      useRecoilValue(timerOverlayAtom)
    const [completedTime, setCompletedTime] = useRecoilState(
      timerOverlayCompletedTimeAtom
    )
    const timerCurrentState = timerOverlayState
    const isCountDownDir = timerDirection === TIMER_DIRECTION.DOWN

    const completedTimeRef = useRef(completedTime)

    // Calculate date depending upon direction
    const date = useMemo(
      () =>
        isCountDownDir
          ? Date.now() + allowedDuration - usedDuration - completedTime
          : Date.now(),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [allowedDuration, usedDuration, isCountDownDir]
    )

    // Calculate now in case of up direction
    const now = useCallback(
      () => Date.now() + usedDuration + completedTimeRef.current,
      [usedDuration]
    )

    const onDownTick: CountdownTimeDeltaFn = useCallback(
      value => {
        setCompletedTime(allowedDuration - usedDuration - value.total)
      },
      [allowedDuration, setCompletedTime, usedDuration]
    )

    const onUpTick: CountdownTimeDeltaFn = useCallback(
      value => {
        const currentTime = Math.abs(value.total)
        setCompletedTime(currentTime)
        if (allowedDuration <= currentTime) {
          logger.log("COMPLETE UP TIMER")
          setIsCompleted(true)
        }
      },
      [allowedDuration, setCompletedTime]
    )

    // For calculating complete in case of down direction
    const onComplete = useCallback(() => {
      logger.log("COMPLETE DOWN TIMER")
      setIsCompleted(true)
    }, [])

    useEffect(() => {
      switch (timerCurrentState) {
        case TIMER_OVERLAY_STATE.START:
          countdownEleRef.current?.api?.start()
          break
        case TIMER_OVERLAY_STATE.PAUSE:
          countdownEleRef.current?.api?.pause()
          break
      }
    }, [timerCurrentState])

    const isSmallWidthMainScreen = isSmallMainScreen || isMobile || isTablet

    return (
      <Flex
        id="overlay-chat"
        p="0.30rem"
        paddingTop={isSmallWidthMainScreen && "0rem"}
        borderRadius="0.6rem"
        borderLeftRadius="0"
        textOverflow="ellipsis"
        flexDirection="column"
        overflow="hidden"
        whiteSpace="nowrap"
        maxW={maxWidth}
        bgColor={
          roomStyles?.commentBackgroundColor
            ? roomStyles.commentBackgroundColor
            : "white"
        }
      >
        {isCompleted ? (
          t("Time up!!!")
        ) : (
          <Countdown
            key={`timerOverlay-${usedDuration}-${timerDirection}`}
            ref={countdownEleRef}
            date={date}
            now={!isCountDownDir ? now : undefined}
            onTick={isCountDownDir ? onDownTick : onUpTick}
            onComplete={onComplete}
            renderer={props => (
              <RenderTimer
                isSmallWidthMainScreen={isSmallWidthMainScreen}
                {...props}
              />
            )}
            // autoStart={false}
            overtime
          />
        )}
      </Flex>
    )
  }
)
